import { SlotProps } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';

import EthereumIcon from '@/assets/icons/ethereum.svg?react';
import PolygonIcon from '@/assets/icons/polygon.svg?react';
import TronIcon from '@/assets/icons/tron.svg?react';
import EmptyIcon from '@/assets/icons/unset.svg?react';
import { PaymentFlowModalMessage } from '@/components/molecules/PaymentFlowModalMessage';
import { WalletAddressInput } from '@/components/molecules/WalletAddressInput';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useTranslator } from '@/hooks/useTranslator';
import { PaymentRailType } from '@/types/currency';
import { TransactionResponse } from '@/types/transaction';

interface Props {
  readonly transaction: TransactionResponse;
}

interface LabelWithIcon {
  readonly label: string;
  readonly icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

export const CryptoAddressModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transaction } = props;
  const { state } = useLocation();
  const { fundingMethod } = state ?? { fundingMethod: PaymentRailType.unset };

  const walletAddress = React.useMemo((): string => {
    const { fundingWallet } = transaction.blockchainInfo;
    if (fundingWallet === null) {
      return '';
    } else {
      return fundingWallet;
    }
  }, [transaction.blockchainInfo]);

  const tr = useTranslator();
  const navigate = useNavigate();

  const onClose = React.useCallback((): void => {
    navigate('/home', { replace: true });
  }, [navigate]);

  const onNext = React.useCallback((): void => {
    navigate(`../hash?transactionId=${transaction.id}`, { replace: true });
  }, [navigate, transaction.id]);

  const chain = React.useMemo((): LabelWithIcon => {
    if (fundingMethod === PaymentRailType.Polygon) {
      return {
        label: 'Polygon',
        icon: PolygonIcon,
      };
    } else if (fundingMethod === PaymentRailType.Ethereum) {
      return {
        label: 'Ethereum',
        icon: EthereumIcon,
      };
    } else if (fundingMethod === PaymentRailType.TRON) {
      return {
        label: 'TRON',
        icon: TronIcon,
      };
    } else {
      return {
        label: 'Unknown',
        icon: EmptyIcon,
      };
    }
  }, [fundingMethod]);

  const chainSlotProps = React.useMemo((): SlotProps<any, any, any> => {
    const Icon = chain.icon;

    return {
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <Icon width={24} />
          </InputAdornment>
        ),
      },
    };
  }, [chain.icon]);

  React.useEffect((): void => {
    if (fundingMethod === PaymentRailType.unset) {
      navigate('/home', { replace: true });
    }
  }, [fundingMethod, navigate]);

  return (
    <ApplicationModal
      title={tr('CRYPTO_TRANSFER_MODAL_TITLE')}
      description={tr('CRYPTO_TRANSFER_MODAL_DESCRIPTION')}
      onClose={onClose}>
      <Box display="flex" flexDirection="column" gap={2}>
        <TextFieldWithIcon
          label={tr('CHAIN')}
          value={chain.label}
          disabled={true}
          fullWidth={true}
          slotProps={chainSlotProps}
        />
        <WalletAddressInput label={tr('ADDRESS')} value={walletAddress} fullWidth={true} />
        <Box my={3}>
          <PaymentFlowModalMessage
            type="info"
            message={[tr('CALIZA_DOES_NOT_CHARGE_DEPOSIT_FEES')]}
          />
        </Box>
        <Box>
          <Button variant="contained" fullWidth={true} onClick={onNext}>
            {tr('NEXT')}
          </Button>
          <ButtonBase sx={styles.cancelButton} disableRipple={true} onClick={onClose}>
            <Typography component="span" fontSize={16} fontWeight={400} color="error.main">
              {tr('CANCEL_TRANSFER')}
            </Typography>
          </ButtonBase>
        </Box>
      </Box>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  cancelButton: {
    display: 'block',
    mx: 'auto',
    mt: 5,
  },
};

const TextFieldWithIcon = styled(TextField)({
  '& .MuiFormLabel-root': {
    left: 14,
  },
});
